// src/components/treasure/mapPages.js
//
// THE TREASURE MAP'S PAGES. A page is a picture plus the trail drawn on it:
//
//   { id, name, imageUrl, aspect (w/h), trail: [[x%, y%], …], regions: [{ to, name, emoji }] }
//
//   trail     points along the dotted path, in % of the picture, from where
//             you walk ONTO the page to where you walk OFF it
//   regions   which part of the trail is which land ("to" = how far along,
//             0–1), so a chest can say "🌵 Desert" or "🌲 Forest"
//
// Five pages are built in (desert, forest, snow, mountains, jungle). Admins
// add more in Layout Builder → Treasure map pages (treasureMapPages/{id});
// they come after the built-in ones. A class's chests are SPLIT across the
// pages; a class with more topics than all pages hold starts over at page 1.

import { useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { seasonOpen } from "../avatar/seasons/seasonData";
import { useSeasonLive } from "../avatar/seasons/seasonLive";

// The five built-in pages (public/treasure-map/*.webp), walked in this order.
// Trails traced from each picture's dotted path: Start sign → … → the X.
export const BUILT_IN_PAGES = [
  {
    id: "builtin-desert",
    name: "Sunny Desert",
    imageUrl: "/treasure-map/desert.webp",
    aspect: 1672 / 941,
    trail: [[8.97, 60.36], [9.98, 60.14], [13.86, 57.15], [18.19, 56.65], [22.14, 60.35], [26.12, 62.45], [30.02, 56.91], [32.52, 51.59], [36.73, 51.48], [40.0, 55.79], [40.8, 60.71], [43.06, 63.76], [51.64, 32.94], [48.46, 35.06], [49.75, 38.2], [53.54, 38.9], [57.55, 39.88], [59.94, 43.91], [56.58, 47.33], [53.11, 48.66], [50.66, 51.12], [51.54, 54.82], [54.88, 55.36], [58.66, 54.87], [62.36, 57.57], [64.1, 61.14], [66.88, 63.84], [70.59, 62.42], [75.27, 60.85], [79.1, 60.69], [82.38, 54.87], [84.5, 51.92], [87.25, 50.78], [89.11, 50.16]],
    regions: [{ to: 1, name: "Sunny Desert", emoji: "🌵" }],
    capacity: 7,
    builtIn: true,
  },
  {
    id: "builtin-forest",
    name: "Waterfall Forest",
    imageUrl: "/treasure-map/forest.webp",
    aspect: 1671 / 941,
    trail: [[9.87, 61.64], [11.08, 62.04], [16.48, 58.54], [21.11, 57.23], [25.39, 62.84], [29.49, 65.44], [33.23, 59.55], [34.2, 54.99], [39.23, 52.94], [42.83, 57.81], [52.66, 59.51], [61.03, 65.52], [65.68, 67.58], [69.86, 65.87], [70.25, 59.6], [69.78, 53.07], [74.26, 50.44], [76.77, 46.51], [73.74, 42.3], [72.76, 37.67], [76.19, 34.44], [80.69, 34.99], [83.03, 38.29], [85.72, 42.62], [90.07, 42.72]],
    regions: [{ to: 1, name: "Waterfall Forest", emoji: "🌲" }],
    capacity: 7,
    builtIn: true,
  },
  {
    id: "builtin-snow",
    name: "Snowy Village",
    imageUrl: "/treasure-map/snow.webp",
    aspect: 1671 / 941,
    trail: [[10.47, 63.55], [11.52, 63.47], [15.92, 62.66], [19.56, 59.89], [23.98, 60.26], [26.78, 64.07], [30.44, 66.85], [33.84, 63.59], [34.47, 56.18], [37.34, 52.57], [41.73, 51.19], [45.35, 57.54], [54.76, 62.17], [66.34, 67.56], [70.06, 65.1], [68.76, 60.1], [69.43, 56.53], [73.16, 55.05], [77.44, 54.07], [78.85, 47.29], [76.74, 42.52], [79.07, 37.98], [82.78, 36.22], [86.59, 37.72], [91.26, 39.11]],
    regions: [{ to: 1, name: "Snowy Village", emoji: "❄️" }],
    capacity: 7,
    builtIn: true,
  },
  {
    id: "builtin-mountain",
    name: "Mountain Peaks",
    imageUrl: "/treasure-map/mountain.webp",
    aspect: 1671 / 941,
    trail: [[9.87, 63.76], [10.8, 63.72], [13.94, 61.46], [17.97, 60.12], [21.41, 63.09], [24.21, 66.77], [28.21, 66.61], [30.1, 60.76], [32.44, 56.88], [36.79, 56.28], [40.3, 58.83], [41.41, 62.84], [47.49, 57.98], [47.67, 51.28], [51.59, 49.47], [56.06, 50.64], [60.12, 49.31], [59.81, 44.6], [59.92, 38.9], [63.43, 37.19], [65.59, 37.45], [68.21, 39.14], [71.7, 42.04], [76.56, 42.05], [77.48, 37.2], [77.77, 32.71], [82.22, 31.03], [87.97, 30.5]],
    regions: [{ to: 1, name: "Mountain Peaks", emoji: "⛰️" }],
    capacity: 7,
    builtIn: true,
  },
  {
    id: "builtin-jungle",
    name: "Jungle Temple",
    imageUrl: "/treasure-map/jungle.webp",
    aspect: 1670 / 941,
    trail: [[10.06, 61.42], [11.03, 61.51], [15.66, 57.77], [19.69, 57.54], [22.57, 61.79], [26.29, 66.46], [30.31, 65.72], [31.41, 59.73], [32.35, 54.91], [36.27, 53.67], [39.03, 57.45], [47.6, 60.57], [56.82, 56.2], [60.26, 53.17], [60.13, 47.11], [57.66, 44.1], [56.13, 40.01], [58.08, 36.3], [61.57, 35.13], [66.13, 37.71], [67.11, 43.06], [69.75, 48.84], [73.86, 49.07], [77.45, 43.74], [79.22, 38.71], [82.91, 36.47], [86.95, 37.39], [89.22, 37.41]],
    regions: [{ to: 1, name: "Jungle Temple", emoji: "🐒" }],
    capacity: 7,
    builtIn: true,
  },
];
export const BUILT_IN_PAGE = BUILT_IN_PAGES[0];

/**
 * Live list of pages: the built-in ones, then the admin's (ordered).
 *
 * HOLIDAY VERSIONS: an admin page with `season` + `replaces` (a built-in
 * page's id) takes that page's place while the season is on sale — e.g. the
 * forest with Christmas lights during Christmas — and is hidden otherwise.
 */
export const useMapPages = () => {
  const seasonTick = useSeasonLive(); // seasons opened / closed by an admin
  const [all, setAll] = useState({ normal: [], holiday: [] });
  useEffect(
    () =>
      onSnapshot(
        collection(db, "treasureMapPages"),
        (snap) => {
          const extra = snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((p) => p.active !== false && p.imageUrl && Array.isArray(p.trail) && p.trail.length >= 2)
            .map((p) => ({ ...p, trail: p.trail.map((pt) => (Array.isArray(pt) ? pt : [pt.x, pt.y])) }))
            .sort((a, b) => (Number(a.order) || 0) - (Number(b.order) || 0));
          setAll({ normal: extra.filter((p) => !p.season), holiday: extra.filter((p) => p.season && p.replaces) });
        },
        () => setAll({ normal: [], holiday: [] })
      ),
    []
  );
  // re-check the seasons every hour too (a season can open while the page is open)
  const [hour, setHour] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setHour((h) => h + 1), 3600000);
    return () => clearInterval(t);
  }, []);
  return useMemo(() => {
    const swap = (p) => all.holiday.find((h) => h.replaces === p.id && seasonOpen(h.season)) || p;
    return [...BUILT_IN_PAGES.map(swap), ...all.normal];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all, seasonTick, hour]);
};

// ---------------------------------------------------------------------------
// geometry along a trail (in % units of its picture)
// ---------------------------------------------------------------------------

/** lengths for a trail, measured in the picture's real shape */
export const measure = (trail, aspect = 16 / 9) => {
  const seg = [];
  let total = 0;
  for (let i = 1; i < trail.length; i += 1) {
    const dx = (trail[i][0] - trail[i - 1][0]) * aspect;
    const dy = trail[i][1] - trail[i - 1][1];
    const len = Math.hypot(dx, dy);
    seg.push(len);
    total += len;
  }
  return { seg, total: total || 1 };
};

/** the point (and direction) f of the way along the trail, 0–1 */
export const pointAt = (trail, m, f) => {
  const want = Math.max(0, Math.min(1, f)) * m.total;
  let run = 0;
  for (let i = 0; i < m.seg.length; i += 1) {
    if (run + m.seg[i] >= want || i === m.seg.length - 1) {
      const t = m.seg[i] ? (want - run) / m.seg[i] : 0;
      const a = trail[i];
      const b = trail[i + 1];
      return { x: a[0] + (b[0] - a[0]) * t, y: a[1] + (b[1] - a[1]) * t, dx: b[0] - a[0], dy: b[1] - a[1] };
    }
    run += m.seg[i];
  }
  const last = trail[trail.length - 1];
  return { x: last[0], y: last[1], dx: 1, dy: 0 };
};

export const regionAt = (page, f) => (page.regions || []).find((r) => f <= r.to) || null;

// ---------------------------------------------------------------------------
// CHEST SPOTS — spaced so nothing overlaps (Nov 2026)
// ---------------------------------------------------------------------------
//
// Chests used to be spread evenly along the trail (f = 1/(n+1), 2/(n+1) …)
// with up to 7 per page. On a trail that bends back on itself (the desert's
// loop) or with 5+ chests, two chests landed on top of each other, and the
// avatar standing at one chest covered the next one.
//
// Now each page works out its own SAFE SPOTS from its trail: walking along
// it, a spot is kept only if that chest (picture + name label), with a gap
// all round, touches no other chest, the exit arrow / X, or the avatar
// standing at any other chest. The number of safe spots IS the page's
// capacity (an admin page's own "chest count" can only lower it). A page
// given fewer chests uses an evenly spread subset of its spots, so they are
// even further apart.
//
// Sizes are fractions of the map's WIDTH, the same as TreasureTrail draws
// them (chest ≈ 6.5%, label up to ≈ 15%, avatar ≈ 13% tall), so the answer
// holds on a smart board, a laptop and the phone layout alike.

export const CHEST_OFFSET = 7.5; // % of the picture's height, beside the trail

/** Where a chest beside the trail sits, in % of the picture. */
export const chestPoint = (trail, m, aspect, f, side) => {
  const p = pointAt(trail, m, f);
  const len = Math.hypot(p.dx * aspect, p.dy) || 1;
  const nx = (-p.dy / len) * side; // normal, in real units
  const ny = ((p.dx * aspect) / len) * side;
  return {
    x: Math.max(4, Math.min(96, p.x + (nx * CHEST_OFFSET) / aspect)),
    y: Math.max(8, Math.min(94, p.y + ny * CHEST_OFFSET)),
    foot: p,
  };
};

// Everything below in units of the map's width (1 = full width).
const SIZE = {
  chestW: 0.065,
  chestH: 0.055,
  labelW: 0.15, // a long topic name; the label is cut off past this
  labelH: 0.024,
  avatarH: 0.13,
  avatarW: 0.08,
  gap: 0.018, // clear space kept between things
  marker: 0.04, // the exit arrow / X at the end of the trail
};
const STEP = 0.004; // how finely the trail is searched
export const START_F = 0.015; // where the avatar stands before its first walk
const F_MIN = 0.05; // keep the first chest off the starting spot
const F_MAX = 0.93; // and the last one off the exit arrow

const boxOverlap = (a, b, gap) => a.l < b.r + gap && b.l < a.r + gap && a.t < b.b + gap && b.t < a.b + gap;

const slotCache = new Map();

/**
 * The safe chest spots on a page: [{ f, side }] along the trail. Cached per
 * trail, so it is worked out once.
 */
export const chestSlots = (page) => {
  const trail = page?.trail || [];
  const aspect = page?.aspect || 16 / 9;
  const key = `${aspect}|${JSON.stringify(trail)}`;
  if (slotCache.has(key)) return slotCache.get(key);
  if (trail.length < 2) {
    const one = [{ f: 0.5, side: -1 }];
    slotCache.set(key, one);
    return one;
  }
  const m = measure(trail, aspect);
  const h = 1 / aspect; // map height in width units
  // TreasureTrail centres the chest button with translate(-50%, -60%)
  const chestBox = (pt) => {
    const cx = pt.x / 100;
    const cy = (pt.y / 100) * h;
    const w = Math.max(SIZE.chestW, SIZE.labelW);
    const tall = SIZE.chestH + SIZE.labelH;
    const top = cy - 0.6 * tall;
    return { l: cx - w / 2, r: cx + w / 2, t: top, b: top + tall };
  };
  // .tt-me: feet on the trail, translate(-50%, -94%)
  const avatarBox = (foot) => {
    const fx = foot.x / 100;
    const fy = (foot.y / 100) * h;
    return { l: fx - SIZE.avatarW / 2, r: fx + SIZE.avatarW / 2, t: fy - 0.94 * SIZE.avatarH, b: fy + 0.06 * SIZE.avatarH };
  };
  const end = pointAt(trail, m, 1);
  const endBox = {
    l: end.x / 100 - SIZE.marker / 2,
    r: end.x / 100 + SIZE.marker / 2,
    t: (end.y / 100) * h - SIZE.marker / 2,
    b: (end.y / 100) * h + SIZE.marker / 2,
  };

  // where a new visitor's avatar stands before walking anywhere (TreasureTrail)
  const startBox = avatarBox(pointAt(trail, m, START_F));

  const kept = []; // { f, side, chest, avatar }
  let lastSide = 1;
  for (let f = F_MIN; f <= F_MAX + 1e-9; f += STEP) {
    // alternate sides for looks; take the other side if the first is crowded
    for (const side of [-lastSide, lastSide]) {
      const pt = chestPoint(trail, m, aspect, f, side);
      const chest = chestBox(pt);
      const avatar = avatarBox(pt.foot);
      if (boxOverlap(chest, endBox, SIZE.gap) || boxOverlap(chest, startBox, SIZE.gap)) continue;
      const clash = kept.some(
        (k) =>
          boxOverlap(chest, k.chest, SIZE.gap) || // two chests / labels
          boxOverlap(chest, k.avatar, SIZE.gap) || // standing at that chest covers this one
          boxOverlap(k.chest, avatar, SIZE.gap) // standing here covers that one
      );
      if (clash) continue;
      kept.push({ f, side, chest, avatar });
      lastSide = side;
      break;
    }
  }
  const slots = kept.length ? kept.map(({ f, side }) => ({ f, side })) : [{ f: 0.5, side: -1 }];
  slotCache.set(key, slots);
  return slots;
};

/** How many chests fit on a page without crowding. */
export const pageCapacity = (page) => {
  const fits = chestSlots(page).length;
  const asked = Number(page?.capacity);
  return Math.max(1, Number.isFinite(asked) && asked > 0 ? Math.min(asked, fits) : fits);
};

/** `count` spots spread evenly over a page's safe spots. */
const pickSlots = (slots, count) => {
  if (count >= slots.length) return slots.slice(0, count);
  return Array.from({ length: count }, (_, j) => slots[Math.round(((j + 0.5) * slots.length) / count - 0.5)]);
};

/** Shares n chests over maps with these capacities, as evenly as they allow. */
const share = (n, caps) => {
  const counts = caps.map(() => 0);
  let left = n;
  // round-robin: every map gets one before any map gets two, and so on
  while (left > 0) {
    let gave = false;
    for (let i = 0; i < caps.length && left > 0; i += 1) {
      if (counts[i] < caps[i]) {
        counts[i] += 1;
        left -= 1;
        gave = true;
      }
    }
    if (!gave) break;
  }
  return counts;
};

/**
 * Lays topics out on pages, each chest on one of its page's safe spots.
 *
 * * Every topic fits on the pages: SPLIT evenly across them (10 topics on 5
 *   pages = 2 per page); a class with only 3 topics uses the first 3 pages.
 * * More topics than all pages hold: the pages REPEAT (desert, forest, snow,
 *   mountains, jungle, desert again …), using as few maps as it takes, and
 *   the chests are split evenly over all of them. Adding more map pictures
 *   in the Layout Builder means fewer repeats.
 *
 * Returns { chests: [{ topic, index, page (map number), f, side }], pageCount }.
 */
export const layoutChests = (topics, pages) => {
  const list = pages.length ? pages : BUILT_IN_PAGES;
  const n = topics.length;
  const caps = list.map(pageCapacity);
  const room = caps.reduce((a, b) => a + b, 0);

  let mapCaps;
  if (n <= room) {
    // the first `used` pages, then more only if a small page can't take its share
    mapCaps = caps.slice(0, Math.max(1, Math.min(list.length, n)));
    let i = mapCaps.length;
    while (mapCaps.reduce((a, b) => a + b, 0) < n && i < list.length) mapCaps.push(caps[i++]);
  } else {
    mapCaps = [];
    let total = 0;
    for (let i = 0; total < n; i += 1) {
      mapCaps.push(caps[i % list.length]);
      total += caps[i % list.length];
    }
  }
  const counts = share(n, mapCaps);

  const out = [];
  let k = 0;
  counts.forEach((count, mapNo) => {
    const spots = pickSlots(chestSlots(pageFor(list, mapNo)), count);
    topics.slice(k, k + count).forEach((topic, j) => {
      out.push({ topic, index: k + j, page: mapNo, f: spots[j].f, side: spots[j].side });
    });
    k += count;
  });
  return { chests: out, pageCount: Math.max(1, counts.length) };
};

export const pageFor = (pages, pageNo) => pages[pageNo % pages.length];

/** "Sunny Desert", then "Sunny Desert · round 2" the second time it comes round. */
export const mapName = (pages, pageNo) => {
  const p = pageFor(pages, pageNo);
  const round = Math.floor(pageNo / Math.max(1, pages.length)) + 1;
  return round > 1 ? `${p?.name || "Map"} · round ${round}` : p?.name || "Map";
};
